import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import BrandLogo from '../../components/BrandLogo';
import {
  getTasks,
  createTask,
  updateTask,
  deleteTask,
} from '../../api/task.api';
import TaskList from './components/TaskList';
import TaskDetail from './components/TaskDetail';
import TaskForm from './components/TaskForm';
import TaskImport from './components/TaskImport';

function TasksPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await getTasks();
      setTasks(data);
    } catch (error) {
      console.error('Error loading tasks:', error);
      showSnackbar('Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddTask = () => {
    setEditingTask(null);
    setAddDialogOpen(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setEditDialogOpen(true);
  };

  const handleViewTask = (task) => {
    setSelectedTask(task);
    if (isMobile) {
      setMobileDrawerOpen(false); // Close drawer on mobile when task is selected
    }
  };

  const handleDeleteTask = async (task) => {
    if (window.confirm(`Are you sure you want to delete "${task.title}"?`)) {
      try {
        await deleteTask(task._id);
        await loadTasks();
        if (selectedTask && selectedTask._id === task._id) {
          setSelectedTask(null);
        }
        showSnackbar('Task deleted successfully');
      } catch (error) {
        console.error('Error deleting task:', error);
        showSnackbar('Failed to delete task', 'error');
      }
    }
  };

  const handleSaveTask = async (taskData) => {
    try {
      if (editingTask) {
        await updateTask(editingTask._id, taskData);
        showSnackbar('Task updated successfully');
      } else {
        await createTask(taskData);
        showSnackbar('Task created successfully');
      }

      await loadTasks();
      setAddDialogOpen(false);
      setEditDialogOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error saving task:', error);
      showSnackbar('Failed to save task', 'error');
    }
  };

  const handleImportTasks = () => {
    setImportDialogOpen(true);
  };

  const handleImportComplete = (result) => {
    setImportDialogOpen(false);
    if (result.success) {
      loadTasks();
      showSnackbar('Tasks imported successfully');
    } else {
      showSnackbar('Failed to import tasks', 'error');
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setEditingTask(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingTask(null);
  };

  const tasksWithId = (tasks || []).map((task) => ({ ...task, id: task._id || task.id }));

  // Mobile layout
  if (isMobile) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Mobile App Bar */}
        <AppBar position="static" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <BrandLogo
                size="small"
                orientation="horizontal"
                showTagline={false}
                iconSize={20}
              />
              <Typography variant="h6" sx={{ ml: 2 }}>
                Tasks
              </Typography>
            </Box>
            <Button
              color="inherit"
              startIcon={<AddIcon />}
              onClick={handleAddTask}
            >
              Add
            </Button>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {selectedTask ? (
            // Task Detail View
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Paper elevation={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  <TaskDetail
                    task={selectedTask}
                    onEdit={handleEditTask}
                    onDelete={handleDeleteTask}
                  />
                </Box>
              </Paper>
            </Box>
          ) : (
            // Empty State
            <Box sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              p: 3,
            }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a Task
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Choose a task from the list to view details
              </Typography>
            </Box>
          )}
        </Box>

        {/* Mobile Drawer for Task List */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: isSmallMobile ? '100%' : 320,
              boxSizing: 'border-box',
            },
          }}
        >
          <Box sx={{
            p: 2, height: '100%', display: 'flex', flexDirection: 'column',
          }}
          >
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <TaskList
                tasks={tasksWithId}
                loading={loading}
                onAdd={handleAddTask}
                onEdit={handleEditTask}
                onDelete={handleDeleteTask}
                onView={handleViewTask}
                onImport={handleImportTasks}
                selectedTask={selectedTask}
              />
            </Box>
          </Box>
        </Drawer>

        {/* Add Task Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              maxHeight: isSmallMobile ? '100%' : '90vh',
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <AddIcon sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Add New Task
            </Typography>
            <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <TaskForm
              task={null}
              onSave={handleSaveTask}
              onCancel={handleCloseAddDialog}
              loading={loading}
            />
          </Box>
        </Dialog>

        {/* Edit Task Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              maxHeight: isSmallMobile ? '100%' : '90vh',
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <AddIcon sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Edit Task
            </Typography>
            <IconButton onClick={handleCloseEditDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <TaskForm
              task={editingTask}
              onSave={handleSaveTask}
              onCancel={handleCloseEditDialog}
              loading={loading}
            />
          </Box>
        </Dialog>

        {/* Import Dialog */}
        <Dialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Import Tasks
            </Typography>
            <IconButton onClick={() => setImportDialogOpen(false)} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <TaskImport
              onClose={() => setImportDialogOpen(false)}
              onImportComplete={handleImportComplete}
            />
          </Box>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 }, height: '100vh', display: 'flex', flexDirection: 'column',
    }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Tasks
      </Typography>

      <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
        {/* Task List */}
        <Grid item xs={12} md={selectedTask ? 8 : 12}>
          <Paper sx={{
            height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
          }}
          >
            <TaskList
              tasks={tasksWithId}
              loading={loading}
              onAdd={handleAddTask}
              onEdit={handleEditTask}
              onDelete={handleDeleteTask}
              onView={handleViewTask}
              onImport={handleImportTasks}
              selectedTask={selectedTask}
            />
          </Paper>
        </Grid>

        {/* Task Detail */}
        {selectedTask && (
          <Grid item xs={12} md={4}>
            <Paper sx={{
              height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
            }}
            >
              <TaskDetail
                task={selectedTask}
                onEdit={handleEditTask}
                onDelete={handleDeleteTask}
              />
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Add Task Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Add New Task
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <TaskForm
            task={null}
            onSave={handleSaveTask}
            onCancel={handleCloseAddDialog}
            loading={loading}
          />
        </Box>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Edit Task
          </Typography>
          <IconButton onClick={handleCloseEditDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <TaskForm
            task={editingTask}
            onSave={handleSaveTask}
            onCancel={handleCloseEditDialog}
            loading={loading}
          />
        </Box>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Import Tasks
          </Typography>
          <IconButton onClick={() => setImportDialogOpen(false)} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <TaskImport
            onClose={() => setImportDialogOpen(false)}
            onImportComplete={handleImportComplete}
          />
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TasksPage;
